import type { DtoolsProposalSummaryRow } from "./dtoolsDisplaySettings";
import type { ProposalCustomerPricingDisplay } from "./proposalCustomerPricing";

function roundMoney(n: number): number {
  if (!Number.isFinite(n)) return 0;
  return Math.round(n * 100) / 100;
}

function isPositiveAmount(n: number): boolean {
  return Number.isFinite(n) && n > 0.004;
}

/** Bracketed discount display e.g. ($9,765). */
export function formatCustomerDiscountAmount(amount: number, formatMoney: (n: number) => string): string {
  if (!isPositiveAmount(amount)) return "";
  const abs = formatMoney(Math.abs(amount));
  return `(${abs})`;
}

export function buildCustomerProposalSummaryRows(input: {
  systemRows: Array<{ key: string; label: string; amount: number }>;
  pricing: ProposalCustomerPricingDisplay;
  formatMoney: (n: number) => string;
}): DtoolsProposalSummaryRow[] {
  const rows: DtoolsProposalSummaryRow[] = [];

  for (const system of input.systemRows) {
    if (!isPositiveAmount(system.amount)) continue;
    rows.push({
      key: system.key,
      label: system.label,
      amount: input.formatMoney(system.amount),
      strong: input.systemRows.length === 1,
    });
  }

  if (input.pricing.showFinalAgreedPrice) {
    const discount = roundMoney(input.pricing.proposalTotal - input.pricing.finalAgreedPrice);
    if (isPositiveAmount(discount)) {
      rows.push({
        key: "discount",
        label: "Discount",
        amount: formatCustomerDiscountAmount(discount, input.formatMoney),
        muted: true,
        dividerBefore: rows.length > 0,
      });
    }
    if (isPositiveAmount(input.pricing.finalAgreedPrice)) {
      rows.push({
        key: "final-price",
        label: "Final Price",
        amount: input.formatMoney(input.pricing.finalAgreedPrice),
        strong: true,
        emphasis: true,
        dividerBefore: rows.length > 0,
      });
    }
  }

  return rows;
}

export function buildSingleSystemCustomerProposalSummaryRows(input: {
  label: string;
  systemTotal: number;
  pricing: ProposalCustomerPricingDisplay;
  formatMoney: (n: number) => string;
}): DtoolsProposalSummaryRow[] {
  return buildCustomerProposalSummaryRows({
    systemRows: [{ key: "system", label: input.label, amount: input.systemTotal }],
    pricing: input.pricing,
    formatMoney: input.formatMoney,
  });
}
